import { useEffect, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import { observer } from 'mobx-react-lite';

import { Modal } from 'components';
import { CT_TOKEN } from 'consts/copyTrading';
import { useHandleConnectClick } from 'hooks';
import { useWithdraw } from 'hooks/meme/useWithdraw';
import { store } from 'store';
import { addSeparators } from 'utils/helpers';

import * as S from './CopyTradingModal.styles';

const PERCENT_PRESETS = [25, 50, 75, 100];

export const WithdrawModal = observer(() => {
  const { account } = store.web3Store;
  const { portfolio } = store.portfolioStore;
  const {
    value,
    setValue,
    isNoValue,
    isSubmitting,
    setIsSubmitting,
    isCopyModalOpen,
    copyAction,
    closeCopyModal,
  } = store.copyFormStore;
  const isOpen = isCopyModalOpen && copyAction === 'withdraw';
  const availableUsd = portfolio?.deposit || 0;

  const { handleConnectClick } = useHandleConnectClick();
  const withdraw = useWithdraw();

  const isInsufficientBalance = !!value && Number(value) > availableUsd;
  const [inputValue, setInputValue] = useState(value);

  useEffect(() => {
    setValue(inputValue.replaceAll(' ', ''));
  }, [inputValue, setValue]);

  useEffect(() => {
    setInputValue(addSeparators(value));
  }, [value]);

  const handleInputChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(ev.target.value);
  };

  const handleMaxClick = () => {
    setInputValue(String(availableUsd));
  };

  const handlePercentClick = (pct: number) => {
    setInputValue(String(((availableUsd * pct) / 100).toFixed(2)));
  };

  const handleWithdrawClick = async () => {
    if (isNoValue || !account) return;
    setIsSubmitting(true);
    await withdraw(Number(inputValue));
    setIsSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={isSubmitting ? () => {} : closeCopyModal} maxWidth={480}>
      <S.ModalHeader>
        <S.ModalTitle>Withdraw {CT_TOKEN.symbol} from Proxy Wallet</S.ModalTitle>
      </S.ModalHeader>

      <S.ModalBody>
        <S.ModalInfo>
          <span>Available Balance</span>
          <span style={{ color: 'rgba(46, 204, 113, 0.9)', fontWeight: 600 }}>
            ${availableUsd.toFixed(2)}
          </span>
        </S.ModalInfo>

        <S.InputGroup>
          <S.InputLabel>Withdraw Amount (USD)</S.InputLabel>
          <S.InputWrapper>
            <NumericFormat
              value={inputValue}
              onChange={handleInputChange}
              inputMode="decimal"
              placeholder="0"
              autoFocus
              thousandSeparator=" "
              allowNegative={false}
              allowedDecimalSeparators={['.', ',']}
              decimalScale={2}
              disabled={isSubmitting}
            />
            <S.MaxButton onClick={handleMaxClick}>MAX</S.MaxButton>
          </S.InputWrapper>
        </S.InputGroup>

        <S.PresetButtonsRow>
          {PERCENT_PRESETS.map((pct) => (
            <S.PresetButton key={pct} onClick={() => handlePercentClick(pct)}>
              {pct}%
            </S.PresetButton>
          ))}
        </S.PresetButtonsRow>

        {(() => {
          if (!account) {
            return <S.ActionButton onClick={handleConnectClick}>Connect wallet</S.ActionButton>;
          }

          if (isSubmitting) {
            return (
              <S.ActionButton $disabled $isLoading>
                ...Submitting
              </S.ActionButton>
            );
          }

          if (isNoValue) {
            return <S.ActionButton $disabled>Enter amount</S.ActionButton>;
          }

          if (isInsufficientBalance) {
            return <S.ActionButton $disabled>Insufficient balance</S.ActionButton>;
          }

          return <S.ActionButton onClick={handleWithdrawClick}>Withdraw</S.ActionButton>;
        })()}

        <S.WarningBox>
          The request is submitted through the copy bot API and settled in {CT_TOKEN.symbol}.
        </S.WarningBox>
      </S.ModalBody>
    </Modal>
  );
});
